import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import {
  RangeSlider,
  Stack,
  Flex,
  Grid,
  useMantineColorScheme,
} from "@mantine/core";
import ProjectNumberInput from "./ProjectNumberInput";
import { formatProjectNumber } from "../../utils/helpers";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { useAtom } from "jotai";

const KPILimitsRange = ({
  initialMin,
  initialMax,
  initialValue,
  onChange,
  onValidityChange,
  rightIcon,
  name,
  introKey,
  disabled = false
}) => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const numberFormat = projectDetails?.projectDTO?.numberFormat;
  const [value, setValue] = useState(initialValue);
  const [minInput, setMinInput] = useState(initialValue[0].toString());
  const [maxInput, setMaxInput] = useState(initialValue[1].toString());
  const grayIconStyle = useMemo(
    () => ({
      padding: "4px",
    }),
    [colorScheme],
  );

  useEffect(() => {
    if (value[0] !== initialValue[0] || value[1] !== initialValue[1]) {
      setValue(initialValue);
      setMinInput(initialValue[0].toString());
      setMaxInput(initialValue[1].toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  const handleChange = useCallback(
    (val) => {
      if (disabled) return;
      if (val[0] !== value[0] || val[1] !== value[1]) {
        setValue(val);
        setMinInput(val[0].toString());
        setMaxInput(val[1].toString());
        if (onChange) onChange(val);
      }
    },
    [onChange, value, disabled]
  );
  const lastValidityRef = useRef(undefined);
  const numberRegex = /^[-+]?\d*$/;
  const handleMinChange = useCallback(
    (e) => {
      if (disabled) return;
      const input = e.target.value;
      setMinInput(input);
      if (input === '' || input === '-') return;
      if (!numberRegex.test(input)) return;
      const val = Number(input);
      if (!isNaN(val)) {
        handleChange([val, Math.max(val, value[1])]);
      }
    },
    [handleChange, value, disabled]
  );

  const handleMaxChange = useCallback(
    (e) => {
      if (disabled) return;
      const input = e.target.value;
      setMaxInput(input);
      if (input === '' || input === '-') return;
      if (!numberRegex.test(input)) return;
      const val = Number(input);
      if (!isNaN(val)) {
        handleChange([Math.min(value[0], val), val]);
      }
    },
    [handleChange, value, disabled]
  );

  const minError =
    value[0] < initialMin || value[0] > initialMax || value[0] === value[1];
  const maxError =
    value[1] < initialMin || value[1] > initialMax || value[0] === value[1];

  // Build field-specific error messages
  const minErrorMessage =
    minInput === '' || minInput === '-'
      ? 'Please enter a valid minimum'
      : !numberRegex.test(minInput)
      ? 'Only numbers are allowed'
      : minError
      ? value[0] === value[1]
        ? 'Minimum must be less than Maximum'
        : `Minimum must be between ${formatProjectNumber(initialMin, { numberFormat })} and ${formatProjectNumber(initialMax, { numberFormat })}`
      : false;

  const maxErrorMessage =
    maxInput === '' || maxInput === '-'
      ? 'Please enter a valid maximum'
      : !numberRegex.test(maxInput)
      ? 'Only numbers are allowed'
      : maxError
      ? value[0] === value[1]
        ? 'Maximum must be greater than Minimum'
        : `Maximum must be between ${formatProjectNumber(initialMin, { numberFormat })} and ${formatProjectNumber(initialMax, { numberFormat })}`
      : false;

  useEffect(() => {
    if (typeof onValidityChange === 'function') {
      const hasError = Boolean(minErrorMessage) || Boolean(maxErrorMessage);
      const isValid = !hasError;
      if (lastValidityRef.current !== isValid) {
        lastValidityRef.current = isValid;
        onValidityChange(isValid);
      }
    }
  }, [minErrorMessage, maxErrorMessage, onValidityChange]);

  return (
    <Grid justify="center" className="mt-2 mb-2">
      <Grid.Col span={10}>
        <Stack gap="sm" spacing="md">
          <RangeSlider
            disabled={disabled}
            min={initialMin}
            max={initialMax}
            step={1}
            value={value}
            onChange={handleChange}
            color={colorScheme === "dark" ? "" : "dark"}
            {...(introKey === 0 ? { 'data-intro': t('kpi.adjustRange'), 'data-position': 'top' } : {})}
            marks={[
              {
                value: initialMin,
                label: formatProjectNumber(initialMin, { numberFormat }),
              },
              {
                value: initialMax,
                label: formatProjectNumber(initialMax, { numberFormat }),
              },
            ]}
          />
          <Flex justify="space-between" gap="md" className="mt-2">
            <ProjectNumberInput
              disabled={disabled}
              label="Minimum"
              type="text"
              value={minInput}
              onChange={handleMinChange}
              {...(introKey === 0 ? { 'data-intro': t('kpi.setMinimum'), 'data-position': 'right' } : {})}
              error={minErrorMessage}
              rightSection={
                <div
                  style={
                    name === "payback"
                      ? {
                          ...grayIconStyle,
                          marginRight: "1rem",
                        }
                      : grayIconStyle
                  }
                >
                  {rightIcon}
                </div>
              }
              rightSectionProps={{ style: { pointerEvents: "none" } }}
            />
            <ProjectNumberInput
              disabled={disabled}
              label="Maximum"
              type="text"
              value={maxInput}
              onChange={handleMaxChange}
              {...(introKey === 0 ? { 'data-intro': t('kpi.setMaximum'), 'data-position': 'left' } : {})}
              error={maxErrorMessage}
              rightSection={
                <div
                  style={
                    name === "payback"
                      ? {
                          ...grayIconStyle,
                          marginRight: "1rem",
                        }
                      : grayIconStyle
                  }
                >
                  {rightIcon}
                </div>
              }
              rightSectionProps={{ style: { pointerEvents: "none" } }}
            />
          </Flex>
        </Stack>
      </Grid.Col>
    </Grid>
  );
};

export default React.memo(KPILimitsRange);
